import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOverview, useDailyActions } from '@/hooks/useAnalytics';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Gem, Activity, Ship, HardHat, ArrowRight, FileText } from 'lucide-react';
import HelpTooltip from '@/components/HelpTooltip';
import { useAuthStore } from '@/stores/auth';
import { useToast, Toast } from '@/components/Toast';
import { pdf } from '@react-pdf/renderer';
import { MonthlySummaryPDF } from '@/lib/pdf/monthlySummary';
import api from '@/lib/api';

function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string | number; sub?: string; icon: React.ElementType }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className="rounded-lg bg-gold/10 p-2.5">
          <Icon size={20} className="text-gold" />
        </div>
      </div>
    </div>
  );
}

const stageColors = ['#C9A96E', '#e8c98a', '#b8956a', '#a3854f', '#8a7040', '#f87171'];

export default function Overview() {
  const { data, isLoading, error } = useOverview();
  const role = useAuthStore((s) => s.user?.role);
  const showDailyActions = role === 'OperationsManager' || role === 'Owner' || role === 'SuperAdmin';
  const { data: dailyActions } = useDailyActions();
  const { toast, show: showToast } = useToast();
  const [reportMonth, setReportMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );

  useEffect(() => {
    document.title = 'Overview — Aditi Diamonds Vault';
  }, []);

  if (isLoading) return <Skeleton />;
  if (error) return <ErrorState retry={() => window.location.reload()} />;
  if (!data) return null;

  const { pipeline, summary, recent_activity } = data;
  const stages = pipeline.by_stage;

  const funnelData = [
    { name: 'Received', count: stages.raw_received.count, weight: stages.raw_received.weight },
    { name: 'Sawing', count: stages.in_sawing.count, weight: stages.in_sawing.weight },
    { name: 'Polishing', count: stages.in_polishing.count, weight: stages.in_polishing.weight },
    { name: 'Polished', count: stages.polished_pending_export.count, weight: stages.polished_pending_export.weight },
    { name: 'Exported', count: stages.exported.count, weight: stages.exported.weight },
    { name: 'Rejected', count: stages.rejected.count, weight: stages.rejected.weight },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">Overview</h1>
        {(role === 'Owner' || role === 'SuperAdmin') && (
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs text-foreground"
            />
            <button
              onClick={async () => {
                showToast('Generating report...', 'loading');
                try {
                  const [y, m] = reportMonth.split('-');
                  const { data: summaryData } = await api.get('/api/analytics/monthly-summary', { params: { year: y, month: m } });
                  const blob = await pdf(<MonthlySummaryPDF {...summaryData} />).toBlob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `monthly-summary-${reportMonth}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  showToast('Downloading...', 'success');
                } catch {
                  showToast('Report generation failed', 'error');
                }
              }}
              disabled={toast.type === 'loading' && toast.visible}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <FileText size={14} /> Monthly Summary
            </button>
          </div>
        )}
      </div>

      {/* Daily actions (Ali / Michael) */}
      {showDailyActions && dailyActions && (
        <div className="rounded-xl border border-gold/20 bg-gold/5 p-5">
          <h2 className="mb-3 text-sm font-semibold text-gold">Today's Actions Needed</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ActionLink count={dailyActions.awaitingAssignment} label="stones ready to assign" to="/dashboard/assign-polisher" />
            <ActionLink count={dailyActions.returnedPending} label="pieces returned, pending recording" to="/dashboard/record-return" />
            <ActionLink count={dailyActions.rejectionsNeedingQC} label="rejections pending QC weighing" to="/dashboard/log-rejection" />
            <ActionLink count={dailyActions.readyForExport} label="pieces ready for export" to="/dashboard/mark-export" />
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Stones"
          value={pipeline.total_stones.toLocaleString()}
          sub={`${pipeline.total_weight_ct.toLocaleString()} ct total`}
          icon={Gem}
        />
        <StatCard
          label="In Progress"
          value={summary.stones_with_polishers}
          sub={`${summary.stones_overdue} overdue (>60d)`}
          icon={Activity}
        />
        <StatCard
          label="Exported"
          value={stages.exported.count.toLocaleString()}
          sub={`${stages.exported.weight.toLocaleString()} ct`}
          icon={Ship}
        />
        <StatCard
          label="Active Workers"
          value={summary.workers_active}
          sub={`${summary.active_packages} active packages`}
          icon={HardHat}
        />
      </div>

      {/* Pipeline funnel */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
          Pipeline
          <HelpTooltip content="Shows how many stones are at each processing stage right now. Stones move from left to right as they are processed." />
        </h2>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={funnelData} barCategoryGap="20%">
            <XAxis dataKey="name" tick={{ fill: '#8a8680', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#8a8680', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#1c1c1f', border: '1px solid #2a2a2e', borderRadius: 8, fontSize: 13 }}
              labelStyle={{ color: '#f0ede8' }}
              itemStyle={{ color: '#C9A96E' }}
              formatter={(value: number, _name: string, props: any) => [
                `${value} stones (${props.payload.weight} ct)`,
                '',
              ]}
            />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {funnelData.map((_, i) => (
                <Cell key={i} fill={stageColors[i]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cost tracking prompt */}
      {role === 'Owner' && data.has_cost_data === false && (
        <CostBanner />
      )}

      {/* By Stone Type */}
      {data.by_stone_type && data.by_stone_type.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold text-foreground">By Stone Type</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4 text-right">Count</th>
                  <th className="pb-2 text-right">Total Weight (ct)</th>
                </tr>
              </thead>
              <tbody>
                {data.by_stone_type.map((st: any) => (
                  <tr key={st.name} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-medium text-foreground">{st.name}</td>
                    <td className="py-2 pr-4 text-right font-mono text-foreground">{st.count}</td>
                    <td className="py-2 text-right font-mono text-foreground">{st.totalWeight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Recent Completions</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="pb-2 pr-4">Serial</th>
                <th className="pb-2 pr-4">Polisher</th>
                <th className="pb-2 pr-4">Shape</th>
                <th className="pb-2 pr-4">Grade</th>
                <th className="pb-2 pr-4 text-right">Weight (ct)</th>
                <th className="pb-2 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {recent_activity.map((item: any, i: number) => (
                <tr
                  key={item.serial}
                  className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-card' : 'bg-secondary/30'}`}
                >
                  <td className="py-2 pr-4 font-mono text-gold">{item.serial}</td>
                  <td className="py-2 pr-4 text-foreground">{item.polisher_name}</td>
                  <td className="py-2 pr-4 text-foreground">{item.shape || '—'}</td>
                  <td className="py-2 pr-4">
                    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      item.grade === 'A' ? 'bg-success/10 text-success'
                      : item.grade === 'B' ? 'bg-gold/10 text-gold'
                      : 'bg-secondary text-muted-foreground'
                    }`}>
                      {item.grade || '—'}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-foreground">{item.finished_weight_ct}</td>
                  <td className="py-2 text-right text-muted-foreground">
                    {new Date(item.finish_date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Toast {...toast} />
    </div>
  );
}

function CostBanner() {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('aditi-cost-banner-dismissed')) setDismissed(true);
  }, []);
  if (dismissed) return null;
  return (
    <div className="flex items-center justify-between rounded-xl border border-gold/20 bg-gold/5 px-5 py-3">
      <p className="text-sm text-foreground">
        <span className="mr-1.5">💡</span>
        Add purchase costs to packages to unlock margin analysis.{' '}
        <Link to="/dashboard/packages" className="font-medium text-gold hover:underline">Go to Packages</Link>
      </p>
      <button
        onClick={() => { setDismissed(true); localStorage.setItem('aditi-cost-banner-dismissed', '1'); }}
        className="ml-4 text-xs text-muted-foreground hover:text-foreground"
      >
        Dismiss
      </button>
    </div>
  );
}

function ActionLink({ count, label, to }: { count: number; label: string; to: string }) {
  if (count === 0) return (
    <div className="text-sm text-muted-foreground/60">0 {label}</div>
  );
  return (
    <Link to={to} className="flex items-center gap-2 text-sm text-foreground hover:text-gold">
      <span className="font-bold text-gold">{count}</span> {label}
      <ArrowRight size={12} className="text-gold" />
    </Link>
  );
}

function Skeleton() {
  return (
    <div className="space-y-6">
      <div className="h-7 w-32 animate-pulse rounded bg-secondary" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 animate-pulse rounded-xl bg-card" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl bg-card" />
      <div className="h-64 animate-pulse rounded-xl bg-card" />
    </div>
  );
}

function ErrorState({ retry }: { retry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <p className="text-danger">Failed to load dashboard data</p>
      <button onClick={retry} className="mt-4 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-[#0a0a0b] hover:bg-gold-light">
        Retry
      </button>
    </div>
  );
}
